import React from "react";
import ContentPage from "../components/ContentPage";

export default function ImportantPage() {
  return (
    <ContentPage title="Important Information">
      <div className="prose prose-invert max-w-none" data-testid="important-content">
        <h2 className="font-heading text-2xl font-semibold text-white mb-4">Account Types &amp; Limits</h2>
        <div className="space-y-4 mb-8">
          <div className="card-glass p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="bg-gray-600 text-white text-xs font-bold px-3 py-1 rounded-full">FREE</span>
              <span className="text-[#E8E4DC] text-sm">72-Hour Trial</span>
            </div>
            <ul className="text-[#E8E4DC] space-y-2 text-sm">
              <li className="flex items-center gap-2"><svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7"/></svg>All premium features unlocked for 72 hours</li>
              <li className="flex items-center gap-2"><svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7"/></svg>1 Administrator account</li>
              <li className="flex items-center gap-2"><svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7"/></svg>1 Guest user account</li>
              <li className="flex items-center gap-2"><svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7"/></svg>No credit card required</li>
              <li className="flex items-center gap-2"><svg className="w-4 h-4 text-[#E8E4DC]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12"/></svg><span className="text-[#E8E4DC]">Features disabled after 72 hours</span></li>
            </ul>
          </div>
          <div className="card-glass p-5 border border-[#A3E635]/30">
            <div className="flex items-center gap-3 mb-3">
              <span className="bg-[#A3E635] text-black text-xs font-bold px-3 py-1 rounded-full">LIFETIME</span>
              <span className="text-[#A3E635] text-sm font-semibold">$18.99 one-time</span>
            </div>
            <ul className="text-[#E8E4DC] space-y-2 text-sm">
              <li className="flex items-center gap-2"><svg className="w-4 h-4 text-[#A3E635]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7"/></svg>All premium features — forever</li>
              <li className="flex items-center gap-2"><svg className="w-4 h-4 text-[#A3E635]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7"/></svg>Up to 10 Administrator / Team users</li>
              <li className="flex items-center gap-2"><svg className="w-4 h-4 text-[#A3E635]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7"/></svg>Up to 3 Guest users</li>
              <li className="flex items-center gap-2"><svg className="w-4 h-4 text-[#A3E635]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7"/></svg>Lifetime updates &amp; new features</li>
              <li className="flex items-center gap-2"><svg className="w-4 h-4 text-[#A3E635]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7"/></svg>Priority support</li>
              <li className="flex items-center gap-2"><svg className="w-4 h-4 text-[#A3E635]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7"/></svg>All platforms: Chrome/Edge, Windows, iOS, Android</li>
            </ul>
          </div>
        </div>

        <h2 className="font-heading text-2xl font-semibold text-white mt-10 mb-4">Platform Compatibility</h2>
        <div className="overflow-x-auto mb-8">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10">
              <th className="text-left p-3 text-[#E8E4DC]">Feature</th>
              <th className="text-center p-3 text-[#E8E4DC]">Chrome/Edge</th>
              <th className="text-center p-3 text-[#E8E4DC]">Windows 11</th>
              <th className="text-center p-3 text-[#E8E4DC]">iOS</th>
              <th className="text-center p-3 text-[#E8E4DC]">Android</th>
            </tr></thead>
            <tbody>
              {[
                ["Always On Top", true, true, false, true],
                ["Custom Frames", true, true, true, true],
                ["Theatre Mode", true, true, true, true],
                ["Multiple Instances", true, true, false, true],
                ["Audio Override", true, true, true, true],
                ["Timer & Alarms", true, true, true, true],
                ["Team Collaboration", true, true, true, true],
                ["Lock Screen Overlay", false, true, false, false],
                ["System Tray", false, true, false, false],
                ["Keyboard Shortcut", false, true, false, false],
              ].map(([feature, ...platforms], i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="p-3 text-[#E8E4DC]">{feature}</td>
                  {platforms.map((supported, j) => (
                    <td key={j} className="text-center p-3">
                      {supported ? <span className="text-[#A3E635]">&#10003;</span> : <span className="text-[#E8E4DC]">&#8212;</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="font-heading text-2xl font-semibold text-white mt-10 mb-4">Usage Tips</h2>
        <ul className="space-y-3 text-[#E8E4DC] mb-8">
          <li className="flex items-start gap-2"><span className="text-[#A3E635] mt-1">&#9679;</span>Top Screen&#8482; frames can be scheduled to appear via Timer &amp; Alarms — great for meetings, market opens, or shift reminders.</li>
          <li className="flex items-start gap-2"><span className="text-[#A3E635] mt-1">&#9679;</span>AOT mode respects page exceptions you configure. Add sites where you don't want overlays to appear.</li>
          <li className="flex items-start gap-2"><span className="text-[#A3E635] mt-1">&#9679;</span>Multiple instances can run simultaneously — monitor your Zoom call, stock ticker, and Slack messages all at once.</li>
          <li className="flex items-start gap-2"><span className="text-[#A3E635] mt-1">&#9679;</span>Use Theatre Mode when presenting or watching content to dim distractions.</li>
          <li className="flex items-start gap-2"><span className="text-[#A3E635] mt-1">&#9679;</span>RGB Backlight frame style is a conversation starter — great for streaming and content creation.</li>
        </ul>

        <h2 className="font-heading text-2xl font-semibold text-white mt-10 mb-4">Data &amp; Privacy</h2>
        <p className="text-[#E8E4DC] mb-4">
          Your account data (email, name, preferences, transaction history) is stored securely on our servers. 
          We collect IP addresses for security purposes only. We never sell your data. 
          All payment processing is handled securely through PayPal — we never see your payment details.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-white mt-10 mb-4">Support</h2>
        <p className="text-[#E8E4DC]">
          For assistance, contact us at <span className="text-[#A3E635]">713.328.9736</span> or email 
          <span className="text-[#A3E635]"> topscreen70126@outlook.com</span>. 
          Visit our Contact page to send a message directly.
        </p>
      </div>
    </ContentPage>
  );
}
